import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useParams } from 'react-router-dom';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../firebase';
import { updateUserRestaurantAssociation } from '../services/restaurant-analytics.service';
import { useApi } from './ApiProvider';
import { logEvent } from 'firebase/analytics';
import { analytics } from '../firebase';
import { useRef } from 'react';

interface RestaurantContextType {
  currentRestaurantId: string | null;
  restaurantName: string | null;
  setRestaurantName: (name: string) => void;
  isRestaurantLoaded: boolean;
}

const RestaurantContext = createContext<RestaurantContextType>({
  currentRestaurantId: null,
  restaurantName: null,
  setRestaurantName: () => {
    return;
  },
  isRestaurantLoaded: false,
});

interface Props {
  children: ReactNode;
}

export const RestaurantContextProvider = ({ children }: Props) => {
  const { restaurantId } = useParams();
  const [user] = useAuthState(auth);
  const [restaurantName, setRestaurantNameState] = useState<string | null>(
    null
  );
  const [isRestaurantLoaded, setIsRestaurantLoaded] = useState(false);
  const api = useApi();

  const setRestaurantName = (name: string) => {
    setRestaurantNameState(name);
    setIsRestaurantLoaded(true);
  };

  // Automatically fetch restaurant name when restaurantId is available
  useEffect(() => {
    console.log('RestaurantContextProvider - restaurantId', restaurantId);
    if (restaurantId && !restaurantName) {
      api
        .getPublicRestaurantData(restaurantId)
        .then((restaurantData: any) => {
          setRestaurantNameState(restaurantData.name);
          setIsRestaurantLoaded(true);
        })
        .catch((error: any) => {
          console.error('Failed to fetch restaurant data for context:', error);
        });
    }
  }, [restaurantId, restaurantName, api]);

  // Update user-restaurant association when restaurant changes
  useEffect(() => {
    if (user && restaurantId && restaurantName) {
      updateUserRestaurantAssociation(user.uid, restaurantId).catch((error) => {
        console.error('Failed to update user-restaurant association:', error);
      });
    }
  }, [user, restaurantId, restaurantName]);

  // Track restaurant visit for anonymous users too
  useEffect(() => {
    if (restaurantId && !user) {
      // For anonymous users, we can still track in localStorage
      localStorage.setItem('lastVisitedRestaurant', restaurantId);
    }
  }, [restaurantId, user]);

  // Track session duration
  const restaurantNameRef = useRef<string | null>(null);

  useEffect(() => {
    if (restaurantName) {
      restaurantNameRef.current = restaurantName;
    }
  }, [restaurantName]);

  useEffect(() => {
    if (!restaurantId) return;

    const startTime = Date.now();
    return () => {
      const duration = (Date.now() - startTime) / 1000;
      // Check if we have a valid ID and a name (captured via ref)
      if (restaurantId && restaurantNameRef.current) {
        logEvent(analytics, 'restaurant_time_spent', {
          restaurant_id: restaurantId,
          restaurant_name: restaurantNameRef.current,
          duration_seconds: duration
        });
      }
    };
  }, [restaurantId]);

  return (
    <RestaurantContext.Provider
      value={{
        currentRestaurantId: restaurantId || null,
        restaurantName,
        setRestaurantName,
        isRestaurantLoaded,
      }}
    >
      {children}
    </RestaurantContext.Provider>
  );
};

export const useRestaurantContext = () => {
  const context = useContext(RestaurantContext);
  if (!context) {
    throw new Error(
      'useRestaurantContext must be used within a RestaurantContextProvider'
    );
  }
  return context;
};
